/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Box, MenuItem } from '@mui/material';
import React from 'react';
import type { WarningListSelectOption } from './WarningListSelect';
import { WarningListSelect } from './WarningListSelect';
import { useWarningsTranslation } from '../../../utils/i18n';

export enum WindForceLevel {
  moderate = 'moderate',
  severe = 'severe',
  extreme = 'extreme',
}

export interface ColorDefinition {
  color: string;
  textColor: string;
  borderColor: string;
}

export const windForceStyles: Record<WindForceLevel, ColorDefinition> = {
  moderate: { color: '#FAE21E', textColor: '#051039', borderColor: '#FBFE02' },
  severe: { color: '#E27000', textColor: '#051039', borderColor: '#FEAE02' },
  extreme: { color: '#D62A20', textColor: '#ffffff', borderColor: '#FF0000' },
};

export interface WindForceOption {
  level: WindForceLevel;
  value: string;
}

export interface WindForceSelectProps {
  value?: string;
  onChange: (value: string) => void;
  gridArea: string;
  options: WindForceOption[];
  isReadOnly?: boolean;
}

export const WindForceSelect: React.FC<WindForceSelectProps> = ({
  onChange,
  value,
  gridArea,
  options,
  isReadOnly,
}: WindForceSelectProps) => {
  const warningListOptions: WarningListSelectOption[] = React.useMemo(() => {
    return options.map((option) => {
      return {
        value: option.value,
        label: option.value,
      };
    });
  }, [options]);

  const { t } = useWarningsTranslation();

  const renderValue = React.useCallback(
    (optionValue: unknown) => {
      const option = options.find((option) => option.value === optionValue);
      const { level } = option!;
      const colors = windForceStyles[level];
      return (
        <Box
          sx={{
            background: colors.color,
            color: colors.textColor,
            width: 'calc(100% + 24px)',
            padding: [1.5, 1],
            marginLeft: -1,
          }}
        >
          {optionValue as string}
        </Box>
      );
    },
    [options],
  );

  const renderOption = React.useCallback(
    (option: WarningListSelectOption, index: number) => {
      const { level } = options[index];
      const colors = windForceStyles[level];
      return (
        <MenuItem
          key={option.value}
          sx={(theme) => ({
            '&.MuiMenuItem-root': {
              padding: 1,
              ...theme.typography.caption,
            },
            '.MuiSelect-select & ': {
              marginLeft: '-8px',
            },
          })}
          value={option.value}
        >
          <Box
            sx={{
              background: colors.color,
              color: colors.textColor,
              width: '100%',
              padding: [1.5, 1],
            }}
          >
            {t('maritime-windforce', { value: option.value })}
          </Box>
        </MenuItem>
      );
    },
    [t, options],
  );

  // change the arrow color for the select when the selected option has the level "extreme"
  const option = options.find((option) => option.value === value);
  const arrowColor =
    option?.level === WindForceLevel.extreme ? '#fff' : undefined;

  return (
    <WarningListSelect
      additionalStyling={{
        '& .MuiSvgIcon-root': {
          fill: arrowColor,
        },
        '& .MuiSelect-standard': {
          paddingBlock: 0,
        },
      }}
      emptyMessage={`(${t('maritime-header-windforce')})`}
      gridArea={gridArea}
      isReadOnly={isReadOnly}
      label={t('maritime-header-windforce')}
      onChange={onChange}
      options={warningListOptions}
      renderOption={renderOption}
      renderValue={renderValue}
      value={value}
    />
  );
};
